// src/state/languageState.js
import { useSyncExternalStore } from "react";
import { safeGet, safeSet } from "./storage";

export const LANG_STORAGE_KEY = "app_language";

// ✅ 같은 탭에서도 언어 변경 감지용(같은 탭은 storage 이벤트 안 뜸)
const __LANG_CHANGE_EVENT__ = "APP_LANGUAGE_CHANGED";

function subscribeLanguage(cb) {
  if (typeof window === "undefined") return () => {};
  const appHandler = () => cb();
  const storageHandler = (event) => {
    if (event?.key && event.key !== LANG_STORAGE_KEY) return;
    cb();
  };
  window.addEventListener(__LANG_CHANGE_EVENT__, appHandler);
  window.addEventListener("storage", storageHandler);
  return () => {
    window.removeEventListener(__LANG_CHANGE_EVENT__, appHandler);
    window.removeEventListener("storage", storageHandler);
  };
}

export const DEFAULT_FLAG_SRC = "/assets/flags/United_Kingdom.svg";

function flagEmojiSrc(emoji) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="20" viewBox="0 0 32 20"><rect width="32" height="20" rx="2" fill="#151a24"/><text x="16" y="14" text-anchor="middle" font-size="14">${emoji}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// ✅ 실제 i18n 리소스가 준비된 언어만 노출한다.
// - ko/en 기본 2개 + 추가 번역 24개 = 총 26개
// - flag 파일이 아직 없는 국가는 깨진 이미지가 뜨지 않도록 inline svg fallback 사용
export const LANGUAGE_OPTIONS = [
  { code: "en", label: "English", flagSrc: "/assets/flags/United_Kingdom.svg" },
  { code: "ko", label: "한국어", flagSrc: "/assets/flags/Korea.svg" },
  { code: "mn", label: "Монгол", flagSrc: "/assets/flags/Mongolia.svg" },
  { code: "ja", label: "日本語", flagSrc: "/assets/flags/Japan.svg" },
  { code: "zh", label: "中文", flagSrc: "/assets/flags/China.svg" },
  { code: "zh-tw", label: "繁體中文", flagSrc: flagEmojiSrc("🇹🇼") },
  { code: "vi", label: "Tiếng Việt", flagSrc: "/assets/flags/Vietnam.svg" },
  { code: "th", label: "ไทย", flagSrc: "/assets/flags/Thailand.svg" },
  { code: "id", label: "Bahasa Indonesia", flagSrc: "/assets/flags/Indonesia.svg" },
  { code: "ms", label: "Bahasa Melayu", flagSrc: "/assets/flags/Malaysia.svg" },
  { code: "tl", label: "Filipino", flagSrc: "/assets/flags/Philippines.svg" },
  { code: "ru", label: "Русский", flagSrc: "/assets/flags/Russia.svg" },
  { code: "es", label: "Español", flagSrc: "/assets/flags/Spain.svg" },
  { code: "fr", label: "Français", flagSrc: "/assets/flags/France.svg" },
  { code: "de", label: "Deutsch", flagSrc: flagEmojiSrc("🇩🇪") },
  { code: "pt", label: "Português", flagSrc: "/assets/flags/Portugal.svg" },
  { code: "it", label: "Italiano", flagSrc: "/assets/flags/Italy.svg" },
  { code: "nl", label: "Nederlands", flagSrc: flagEmojiSrc("🇳🇱") },
  { code: "pl", label: "Polski", flagSrc: flagEmojiSrc("🇵🇱") },
  { code: "uk", label: "Українська", flagSrc: flagEmojiSrc("🇺🇦") },
  { code: "hi", label: "हिन्दी", flagSrc: "/assets/flags/India.svg" },
  { code: "ar", label: "العربية", flagSrc: flagEmojiSrc("🇸🇦") },
  { code: "tr", label: "Türkçe", flagSrc: "/assets/flags/Turkey.svg" },
  { code: "bn", label: "বাংলা", flagSrc: "/assets/flags/Bangladesh.svg" },
  { code: "ur", label: "اردو", flagSrc: "/assets/flags/Pakistan.svg" },
  { code: "fa", label: "فارسی", flagSrc: flagEmojiSrc("🇮🇷") },
];

// ✅ 지원 언어 목록
export const DEFAULT_SUPPORTED_LANGS = LANGUAGE_OPTIONS.map((x) => x.code);
function normalizeLang(code) {
  if (!code) return "";
  let s = String(code).trim();
  if (!s) return "";

  // pt_BR -> pt-br
  s = s.replace(/_/g, "-").toLowerCase();

  // ✅ 기존 저장값/브라우저/서버 alias 통일
  if (s === "kr" || s.startsWith("ko")) return "ko";
  if (s === "fil" || s === "ph" || s.startsWith("fil-")) return "tl";
  if (s === "zh-tw" || s === "zh-hant" || s === "zh-hk" || s === "zh-mo") return "zh-tw";
  if (s === "zh-cn" || s === "zh-sg" || s === "zh-hans") return "zh";
  if (s === "pt-br" || s === "pt-pt") return "pt";
  if (s === "es-mx" || s === "es-es") return "es";

  return s;
}

// ✅ supported 안에서 최적 매칭:
// 1) exact match (zh-tw 등 유지)
// 2) base match (de-de -> de)
function normalizeToSupported(code, supported) {
  const full = normalizeLang(code);
  if (!full) return "";
  if (supported.includes(full)) return full;

  const base = full.split("-")[0];
  if (supported.includes(base)) return base;

  return "";
}

export function detectBrowserLang(supported = DEFAULT_SUPPORTED_LANGS) {
  if (typeof navigator === "undefined") return "en";

  const candidates = [];
  if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages);
  if (navigator.language) candidates.push(navigator.language);

  for (const c of candidates) {
    const matched = normalizeToSupported(c, supported);
    if (matched) return matched;
  }
  return supported.includes("en") ? "en" : supported[0] || "en";
}

// useSyncExternalStore getSnapshot must return the SAME object reference while
// the language value/source are unchanged; otherwise React can re-render forever.
const languageSnapshotCache = new Map();
function stableLanguageSnapshot(current, source) {
  const key = `${source}\u0000${current}`;
  if (!languageSnapshotCache.has(key)) {
    languageSnapshotCache.set(key, Object.freeze({ current, source }));
  }
  return languageSnapshotCache.get(key);
}

export function readLanguageSnapshot(supported = DEFAULT_SUPPORTED_LANGS) {
  const storedRaw = safeGet(LANG_STORAGE_KEY, "");
  const stored = normalizeToSupported(storedRaw, supported);
  if (stored) return stableLanguageSnapshot(stored, "user_setting");

  // 저장된 사용자 언어가 전혀 없으면 브라우저 언어와 무관하게 영어를 기본값으로 사용한다.
  const fallback = supported.includes("en") ? "en" : supported[0] || "en";
  return stableLanguageSnapshot(fallback, "default");
}

export function setLanguageToStorage(lang) {
  const next = normalizeLang(lang) || 'en';
  safeSet(LANG_STORAGE_KEY, next);
  try {
    window.dispatchEvent(new CustomEvent(__LANG_CHANGE_EVENT__, {
      detail: { language: next },
    }));
  } catch {
    try { window.dispatchEvent(new Event(__LANG_CHANGE_EVENT__)); } catch {}
  }
}

export function languageReducer(state, action) {
  switch (action.type) {
    case "LANG/SET": {
      const next = normalizeLang(action.payload || "");
      if (!next) return state;
      setLanguageToStorage(next);
      return { current: next, source: "user_setting" };
    }
    case "LANG/SET_FROM_SERVER": {
      // 서버 프로필 기반 언어(로그인 시)
      const next = normalizeLang(action.payload || "");
      if (!next) return state;

      // 유저가 이미 로컬에서 선택했다면 서버값으로 덮지 않음
      if (state?.source === "user_setting") return state;

      setLanguageToStorage(next);
      return { current: next, source: "server_profile" };
    }
    default:
      return state;
  }
}
// ✅ storage 기반 스냅샷 훅 (RuntimeStateProvider의 useLanguageState와 이름 충돌 방지)
export function useLanguageStorageState(supported = DEFAULT_SUPPORTED_LANGS) {
  return useSyncExternalStore(
    subscribeLanguage,
    () => readLanguageSnapshot(supported),
    () => readLanguageSnapshot(supported)
  );
}
/**
 * @deprecated
 * - "언어 소스"는 RuntimeStateProvider의 useLanguageState를 쓰는 것을 권장
 * - 기존 코드 호환을 위해 alias만 유지
 */
export const useLanguageState = useLanguageStorageState;
